import { useEffect, useLayoutEffect, useRef, useState } from "react"
import cn from "clsx"
import { map } from "rxjs/operators"
import { Skeletons, TooltipShorten, Typography, palette } from "@callcenter/design-system"
import { DateService, serverTimeService } from "@callcenter/services"

import styles from "./time.module.pcss"

const TRANSITION_DURATION = 200
const RENDER_TIME_DURATION = 1500

type Phase = "skeleton" | "fading" | "visible"

type TimeProps = {
  isShowTimezone?: boolean
}

const CurrentTime = ({ isShowTimezone }: Required<TimeProps>) => {
  const [time, setTime] = useState({
    ts: "",
    zone: ""
  })
  const [width, setWidth] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const zoneValue = `${time.zone} time`

  useLayoutEffect(() => {
    if (wrapperRef.current) {
      setWidth(wrapperRef.current.getBoundingClientRect().width)
    }
    const subscription = serverTimeService.timer
      .pipe(
        map(({ ts, offset, tz }) => {
          const tsLocal = DateService.fromUnix(ts + offset).utc()
          const str = tsLocal.format("MMM dd, HH:mm")

          return { ts: str, zone: tz }
        })
      )
      .subscribe(({ ts, zone }) => {
        setTime((prevState) => {
          if (prevState.ts === ts && prevState.zone === zone) {
            return prevState
          }

          return { ts, zone }
        })
      })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  return (
    <Typography.Small
      className={cn("current-time-wrapper", { "current-time-wrapper--single": !isShowTimezone })}
      ref={wrapperRef}
    >
      {Boolean(width) && (
        <>
          <TooltipShorten maxWidth={width} title={time.ts}>
            <Typography.Small color={palette.gray8}>{time.ts}</Typography.Small>
          </TooltipShorten>
          {isShowTimezone ? (
            <TooltipShorten maxWidth={width} title={zoneValue}>
              <Typography.Small color={palette.gray8}>{zoneValue}</Typography.Small>
            </TooltipShorten>
          ) : null}
        </>
      )}
    </Typography.Small>
  )
}

export const Time = ({ isShowTimezone = true }: TimeProps) => {
  const [phase, setPhase] = useState<Phase>("skeleton")

  useEffect(() => {
    const fadeOutTimeout = setTimeout(() => {
      setPhase("fading")
    }, RENDER_TIME_DURATION)

    const showClockTimeout = setTimeout(() => {
      setPhase("visible")
    }, RENDER_TIME_DURATION + TRANSITION_DURATION)

    return () => {
      clearTimeout(fadeOutTimeout)
      clearTimeout(showClockTimeout)
    }
  }, [])

  return (
    <div className={cn(styles.time, { [styles.hiding]: phase === "fading" })}>
      <div className={styles.currentTime}>
        {phase === "visible" ? (
          <CurrentTime isShowTimezone={isShowTimezone} />
        ) : (
          <>
            <Skeletons.SkeletonLine width="80px" height="12px" />
            <Skeletons.SkeletonLine width="120px" height="12px" />
          </>
        )}
      </div>
    </div>
  )
}
